import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Camera, Loader2, X } from "lucide-react";

export function SoLoadPhotosCell({ so, onChanged }) {
  const [busy, setBusy] = useState(false);
  const photos = so.load_photos || [];

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const d = await uploadDocument(f);
        await api.post(`/sales-orders/${so.id}/load-photos`, { name: d.name || f.name, url: d.url, path: d.path });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added to ${so.so_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Photo upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (i) => {
    try {
      await api.delete(`/sales-orders/${so.id}/load-photos/${i}`);
      toast.success("Photo removed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to remove photo");
    }
  };

  const onPick = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; upload(fs); };

  const addButtons = (kind) => (
    <label className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]" title="Take a photo or upload from your photo library" data-testid={`so-load-photos-add-${kind}-${so.id}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
      Add Photos
      <input type="file" className="hidden" accept="image/*" multiple onChange={onPick} />
    </label>
  );

  if (!photos.length) return addButtons("empty");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#002FA7] hover:underline" onClick={(e) => e.stopPropagation()} data-testid={`so-load-photos-btn-${so.id}`}>
          <Camera className="h-3.5 w-3.5" />
          {photos.length} photo{photos.length > 1 ? "s" : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Load Photos — {so.so_number}</p>
        <div className="mb-2 grid grid-cols-3 gap-2" data-testid={`so-load-photos-grid-${so.id}`}>
          {photos.map((p, i) => (
            <div key={`${p.path}-${i}`} className="group relative">
              <a href={p.url} target="_blank" rel="noreferrer" title={p.name} data-testid={`so-load-photo-${so.id}-${i}`}>
                <img src={p.url} alt={p.name} className="h-20 w-full rounded-sm border border-slate-200 object-cover" loading="lazy" />
              </a>
              <button onClick={() => remove(i)}
                className="absolute -right-1.5 -top-1.5 hidden rounded-full border border-slate-200 bg-white p-0.5 text-slate-500 shadow-sm hover:text-red-600 group-hover:block"
                title="Remove photo" data-testid={`so-load-photo-remove-${so.id}-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        {addButtons("popover")}
      </PopoverContent>
    </Popover>
  );
}
